import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';

// Pages
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import PendingPage from './pages/PendingPage';
import DashboardPage from './pages/DashboardPage';
import SubjectsPage from './pages/SubjectsPage';
import StudyPage from './pages/StudyPage';
import QuizPage from './pages/QuizPage';
import ProgressPage from './pages/ProgressPage';
import AchievementsPage from './pages/AchievementsPage';
import RevisionsPage from './pages/RevisionsPage';
import SettingsPage from './pages/SettingsPage';
import ProfilePage from './pages/ProfilePage';
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminStudentsPage from './pages/admin/AdminStudentsPage';
import AdminApprovalsPage from './pages/admin/AdminApprovalsPage';
import AdminSchoolsPage from './pages/admin/AdminSchoolsPage';
import AdminAnalyticsPage from './pages/admin/AdminAnalyticsPage';
import AdminComparisonPage from './pages/admin/AdminComparisonPage';
import AdminWeeklyReportPage from './pages/admin/AdminWeeklyReportPage';
import AdminBrandingPage from './pages/admin/AdminBrandingPage';
import AdminBannersPage from './pages/admin/AdminBannersPage';
import AdminSupportPage from './pages/admin/AdminSupportPage';
import AdminAnnouncementsPage from './pages/admin/AdminAnnouncementsPage';
import AdminContentPage from './pages/admin/AdminContentPage';
import AdminContentReviewPage from './pages/admin/AdminContentReviewPage';
import AdminReportsPage from './pages/admin/AdminReportsPage';
import { SchoolAuthProvider, useSchoolAuth } from './context/SchoolAuthContext';
import SchoolLoginPage from './pages/school/SchoolLoginPage';
import SchoolDashboardPage from './pages/school/SchoolDashboardPage';
import SchoolStudentsPage from './pages/school/SchoolStudentsPage';
import SchoolAddStudentPage from './pages/school/SchoolAddStudentPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminAdminsPage from './pages/admin/AdminAdminsPage';

// Common
import LoadingScreen from './components/common/LoadingScreen';

import SchoolSignupPage from './pages/SchoolSignupPage';

// ── Protected route for active students ───────────────────────
const StudentRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (window.location.pathname.startsWith('/admin')) return children;
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/" replace />;
  if (user.status === 'pending') return <Navigate to="/pending" replace />;
  if (user.status === 'blocked') return <Navigate to="/?blocked=true" replace />;
  return children;
};

// ── School route — redirect to school login if not authenticated ──
const SchoolRoute = ({ children }) => {
  const { school } = useSchoolAuth();
  if (!school) return <Navigate to="/school/login" replace />;
  return children;
};

// ── Public route — redirect if already logged in ──────────────
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user?.status === 'active') return <Navigate to="/dashboard" replace />;
  if (user?.status === 'pending') return <Navigate to="/pending" replace />;
  return children;
};

const AppRoutes = () => (
  <Routes>
    {/* Public */}
    <Route path="/" element={<PublicRoute><LoginPage /></PublicRoute>} />
    <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
    <Route path="/signup/school" element={<SchoolSignupPage />} />
    <Route path="/pending" element={<PendingPage />} />

    {/* Student — protected */}
    <Route path="/dashboard" element={<StudentRoute><DashboardPage /></StudentRoute>} />
    <Route path="/subjects" element={<StudentRoute><SubjectsPage /></StudentRoute>} />
    <Route path="/subjects/:subjectId" element={<StudentRoute><SubjectsPage /></StudentRoute>} />
    <Route path="/study/:topicId" element={<StudentRoute><StudyPage /></StudentRoute>} />
    <Route path="/quiz/:topicId" element={<StudentRoute><QuizPage /></StudentRoute>} />
    <Route path="/progress" element={<StudentRoute><ProgressPage /></StudentRoute>} />
    <Route path="/achievements" element={<StudentRoute><AchievementsPage /></StudentRoute>} />
    <Route path="/revisions" element={<StudentRoute><RevisionsPage /></StudentRoute>} />
    <Route path="/settings" element={<StudentRoute><SettingsPage /></StudentRoute>} />
    <Route path="/profile" element={<Navigate to="/settings" replace />} />

    {/* Admin */}
    <Route path="/admin" element={<AdminLoginPage />} />
    <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
    <Route path="/admin/students" element={<AdminStudentsPage />} />
    <Route path="/admin/approvals" element={<AdminApprovalsPage />} />
    <Route path="/admin/schools" element={<AdminSchoolsPage />} />
    <Route path="/admin/branding" element={<AdminBrandingPage />} />
    <Route path="/admin/banners" element={<AdminBannersPage />} />
    <Route path="/admin/support" element={<AdminSupportPage />} />
    <Route path="/admin/announcements" element={<AdminAnnouncementsPage />} />
    <Route path="/admin/content" element={<AdminContentPage />} />
    <Route path="/admin/reports" element={<AdminReportsPage />} />
    <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />
    <Route path="/admin/comparison" element={<AdminComparisonPage />} />
    <Route path="/admin/weekly-reports" element={<AdminWeeklyReportPage />} />
    <Route path="/school/login" element={<SchoolLoginPage />} />
    <Route path="/school/dashboard" element={<SchoolRoute><SchoolDashboardPage /></SchoolRoute>} />
    <Route path="/school/students" element={<SchoolRoute><SchoolStudentsPage /></SchoolRoute>} />
    <Route path="/school/add-student" element={<SchoolRoute><SchoolAddStudentPage /></SchoolRoute>} />
                                    <Route path="/admin/content/review" element={<AdminContentReviewPage />} />
    <Route path="/admin/settings" element={<AdminSettingsPage />} />
    <Route path="/admin/admins" element={<AdminAdminsPage />} />

    {/* Fallback */}
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

const App = () => (
  <BrowserRouter>
    <ThemeProvider>
      <AuthProvider>
      <SchoolAuthProvider>
                    <ToastProvider>
          <AppRoutes />
        </ToastProvider>
              </SchoolAuthProvider>
    </AuthProvider>
    </ThemeProvider>
  </BrowserRouter>
);

export default App;
